import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass';
import styled from 'styled-components';
import { KnowledgeBaseConfig, FeatureToggle, Analytics } from '../sections';
import {
  Row,
  Heading,
  Loading,
  Section,
  Stepper,
  Subtitle,
  Link,
  FeatureGate,
  AutomationList,
  RecommendedActionsKBDoc,
} from '../components';
import api from '../api';
import { useAppState, useNotification } from '../hooks';

const LINK_SETUP_KB = 'https://developers.liveperson.com/knowledge-base-overview.html';
const LINK_SETUP_RECOMMENDATIONS = 'https://developers.liveperson.com/conversation-orchestrator-agent-assist-setting-up-knowledge-base-recommendations.html';
const LINK_TEST_RECOMMENDATIONS = 'https://developers.liveperson.com/conversation-orchestrator-agent-assist-testing.html';

const HiddenElement = styled(Flex)`
  display: none !important;
`;

function RecommendedActionsKB(props) {
  const [knowledgeBaseList, setKnowledgeBaseList] = useState([]);
  const [configData, setConfigData] = useState(null);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [isFetchingConfig, setIsFetchingConfig] = useState(false);
  const isMounted = useRef(false);

  const {
    features, accountId: brandId, themeMode, isLoading: isLoadingAppState,
  } = useAppState();
  const { addNotification } = useNotification();

  const getKnowledgeBaseList = async () => {
    if (isFetchingList) {
      return;
    }
    if (isMounted.current) {
      setIsFetchingList(true);
    }
    try {
      const data = features['maven.automationlookup.v2']
        ? await api.automationLookup.getKBListV2(brandId)
        : await api.automationLookup.getKBList(brandId);
      if (isMounted.current) {
        setKnowledgeBaseList(data);
      }
    } catch (error) {
      // NOTE: temporary fix
      // The server returns a 401 error code when the API key is empty, and
      // the error message is misleading, so don't show the error for now
      if (isMounted.current) {
        setKnowledgeBaseList([]);
        if (error.response && error.response.status !== 401) {
          addNotification(error, 'error');
        }
      }
    }
    if (isMounted.current) {
      setIsFetchingList(false);
    }
  };

  const saveConfig = async (config) => {
    if (!isFetchingConfig) {
      setIsFetchingConfig(true);
      try {
        await api.automationLookup.createKBConfig(brandId, config);
        setConfigData(config);
        if (config) {
          getKnowledgeBaseList();
        } else {
          setKnowledgeBaseList([]);
        }
        if (isMounted.current) {
          addNotification('API Key saved', 'success');
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsFetchingConfig(false);
      }
    }
  };

  const resetConfig = async () => {
    if (!isFetchingConfig) {
      setIsFetchingConfig(true);
      try {
        const data = await api.automationLookup.getKBConfig(brandId);
        if (isMounted.current) {
          setConfigData(data);
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsFetchingConfig(false);
      }
    }
  };

  const getConfig = async () => {
    if (!isFetchingConfig) {
      setIsFetchingConfig(true);
      try {
        const config = await api.automationLookup.getKBConfig(brandId);
        if (isMounted.current) {
          setConfigData(config);
        }
        if (config) {
          getKnowledgeBaseList();
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsFetchingConfig(false);
      }
    }
  };

  const kbListOld = knowledgeBaseList.map((item, index) => (
    <Row
      key={item.id}
      data={item}
      useIcon
      rowType="list"
      isLast={index + 1 === knowledgeBaseList.length}
      themeMode={themeMode}
    />
  ));

  useEffect(() => {
    async function fetchData() {
      await getConfig();
    }
    if (!isLoadingAppState) {
      fetchData();
    }
  }, [isLoadingAppState]); // eslint-disable-line

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Box>
      <Heading>Knowledge Base Recommendations</Heading>
      <Subtitle mb={3}>
        Scale agent conversations by recommending articles to send to consumers,
        based on the consumer’s intent
      </Subtitle>
      <Section
        section="kbs-getting-started"
        title="Getting started with article recommendations"
        subtitle="Setting up recommendations requires an active knowledge base"
      >
        <Stepper
          nocheck
          steps={[
            {
              id: 1,
              title: (
                <Box>
                  <Link href={LINK_SETUP_KB} target="_blank" rel="noopener noreferrer">Set up knowledge bases</Link>
                </Box>
              ),
            },
            {
              id: 2,
              title: (
                <Box>
                  <Link href={LINK_SETUP_RECOMMENDATIONS} target="_blank" rel="noopener noreferrer">Set up recommendations</Link>
                </Box>
              ),
            },
            {
              id: 3,
              title: (
                <Box>
                  <Link href={LINK_TEST_RECOMMENDATIONS} target="_blank" rel="noopener noreferrer">Test recommendations</Link>
                </Box>
              ),
            },
          ]}
          activeStep={2}
        />
      </Section>
      <FeatureToggle
        addNotification={addNotification}
        brandId={brandId}
        themeMode={themeMode}
      />
      {configData && (
        <FeatureGate feature="maven.feature.analytics">
          <Analytics
            title="Article recommendation usage"
            subtitle="The number of times an agent used an article recommendation in a conversation"
            addNotification={addNotification}
            brandId={brandId}
            type="knowledgeBase"
          />
        </FeatureGate>
      )}
      {/* We should hide the KB API KEY field (СМ-2942),
          afterwards this block will be removed completely */}
      <HiddenElement>
        {configData && (
          <KnowledgeBaseConfig
            isFetchingConfig={isFetchingConfig}
            data={configData}
            saveConfig={saveConfig}
            resetConfig={resetConfig}
          />
        )}
      </HiddenElement>
      <Section
        title="Configure article recommendations"
        subtitle="Enable knowledge bases and select skills to trigger article recommendations based on consumer’s intent"
        mb={3}
      >
        {isFetchingList ? (
          <Flex align="center" justifyContent="center" py={3}>
            <Loading size={24} data-testid="loading-kbs" />
          </Flex>
        ) : (
          <Box>
            <FeatureGate
              feature="maven.automationlookup.v2"
              fallback={kbListOld}
            >
              <Box>
                {knowledgeBaseList.length ? (
                  <AutomationList
                    automations={knowledgeBaseList}
                    themeMode={themeMode}
                    getAutomations={getKnowledgeBaseList}
                    addNotification={addNotification}
                    brandId={brandId}
                    automationType="kb"
                  />
                ) : (
                  <RecommendedActionsKBDoc />
                )}
              </Box>
            </FeatureGate>
          </Box>
        )}
      </Section>
    </Box>
  );
}

export default RecommendedActionsKB;
